import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import U2000ComboBox from "../../../u2000Core/components/formControls/combobox/U2000ComboBox";
import U2000InputCurrency from "../../../u2000Core/components/formControls/input/U2000InputCurrency";
import U2000InputDecimal from "../../../u2000Core/components/formControls/input/U2000InputDecimal";
import U2000InputText from "../../../u2000Core/components/formControls/input/U2000InputText";
import U2000InputCalendarCustom from "../../../u2000Core/components/formControls/input/U2000InputCalendarCustom";
import { U2000FormStateEnumState } from "../../../u2000Core/reducer/formReducer/U2000FormState";
import type { U2000FormStateHandlers } from "../../../u2000Core/reducer/formReducer/U2000FormStateHandlers";
import U2000ActionBtn from "../../../u2000Core/components/buttons/U2000ActionBtn";
import U2000CheckBox from "../../../u2000Core/components/formControls/checkbox/U2000CheckBox";
import { useU3129Context } from "../../U3129Context";
import { useU3129FindProjectHandlers } from "../../U3129UseFindProjectSubprojectHandlers";
import {
  newHonorariumClientRowId,
  type U3129ExpenseItem,
  type U3129ExpenseTypeItem,
  type U3129FindProjectItem,
  type U3129TaxRateItem,
  type U3129Timesheet,
  u3129ComputeExpenseLineAmountFromUnit,
  u3129ExpenseTypeLabel,
  u3129ExpenseTypeUnitCost,
  u3129FormatTaxRateComboLabel,
  u3129IsExpenseTypeActive,
  u3129IsTaxRateActive,
} from "../../U3129Service";
import { NS } from "../../U3129";
import { U3129LineProjectSubprojectRowCells } from "./U3129LineProjectSubprojectRowCells";
import { u3129FormatLineDateDisplay } from "../../tools/U3129FormatLineDate";
import { U3129_READONLY_TABLE_CELL_BOX_COMPACT_CLASS } from "../../tools/u3129ReadonlyTableCellClass";
import {
  U3129_GRID_COMPACT_DECIMAL_CONTROL_CLASS,
  U3129_GRID_COMPACT_ROW_CELL_PY,
  U3129_GRID_COMPACT_ROW_CHECKBOX_TD_CLASS,
  U3129_GRID_COMPACT_TABLE_CONTROL_CLASS,
} from "../../tools/u3129TimesheetGridCompactStyles";
import { formatCurrency } from "../../../u2000Core/tools/U2000CurrencyUtils";
import { FaCheck, FaPlus, FaTimesCircle, FaTrash } from "react-icons/fa";

const U3129_EXPENSE_DATE_COL_CLASS = "w-[9rem] min-w-[9rem] max-w-[9rem]";
const U3129_EXPENSE_PROJECT_COL_CLASS = "w-[8%] min-w-48";
const U3129_EXPENSE_CUSTOMER_COL_CLASS = "w-[22%] min-w-[16rem]";
const U3129_EXPENSE_SUBPROJECT_COL_CLASS = "w-[8%] min-w-40";
const U3129_EXPENSE_TYPE_COL_CLASS = "min-w-48";
const U3129_EXPENSE_TAX_COL_CLASS = "min-w-40";
const U3129_EXPENSE_REMARK_COL_CLASS = "min-w-40";
const U3129_EXPENSE_UNIT_COL_CLASS = "w-24 min-w-28";
const U3129_EXPENSE_QTY_COL_CLASS = "w-20 min-w-26";
const U3129_EXPENSE_AMOUNT_COL_CLASS = "w-36 min-w-36";
const U3129_EXPENSE_TOTAL_LABEL_COL_CLASS = "min-w-280";

function expenseRowShowsUnitCostField(row: U3129ExpenseItem | undefined): boolean {
  const u = row?.unitCost;
  return u != null && typeof u === "number" && Number.isFinite(u);
}

const U3129_READONLY_NUM_BOX_CLASS = `${U3129_READONLY_TABLE_CELL_BOX_COMPACT_CLASS} justify-end tabular-nums`;

function u3129FormatExpenseCurrencyCell(value: unknown): string {
  const n = typeof value === "number" && Number.isFinite(value) ? value : 0;
  return formatCurrency(n, { decimalDigits: 2 });
}

function u3129ExpenseTypeDisplay(expenseTypeId: string | undefined, options: U3129ExpenseTypeItem[]): string {
  if (!expenseTypeId?.trim()) return "—";
  const item = options.find((d) => d.id === expenseTypeId);
  return item ? u3129ExpenseTypeLabel(item) : expenseTypeId;
}

function u3129ExpenseTaxDisplay(taxRateId: string | undefined, options: U3129TaxRateItem[]): string {
  if (!taxRateId?.trim()) return "—";
  const item = options.find((d) => d.id === taxRateId);
  return item ? u3129FormatTaxRateComboLabel(item) : taxRateId;
}

function emptyExpenseRow(): U3129ExpenseItem {
  return {
    clientRowId: newHonorariumClientRowId(),
    transactionDate: null,
    projectId: "",
    subprojectId: "",
    expenseTypeId: "",
    unitCost: null,
    taxRateId: "",
    quantity: undefined,
    amount: 0,
    remark: "",
  };
}

interface U3129ExpenseTabProps {
  formStateHandlers: U2000FormStateHandlers<U3129Timesheet>;
}

interface ExpenseRowEditorProps {
  index: number;
  formStateHandlers: U2000FormStateHandlers<U3129Timesheet>;
  findProjectHandlers: ReturnType<typeof useU3129FindProjectHandlers>;
  expenseTypeOptions: U3129ExpenseTypeItem[];
  taxRateOptions: U3129TaxRateItem[];
  readonly: boolean;
  checkboxDisabled?: boolean;
  showSubProjectColumn: boolean;
  rowIncludeSubProject: boolean;
  getProjectId: () => string | undefined | null;
}

function ExpenseRowEditor({
  index,
  formStateHandlers,
  findProjectHandlers,
  expenseTypeOptions,
  taxRateOptions,
  readonly,
  checkboxDisabled,
  showSubProjectColumn,
  rowIncludeSubProject,
  getProjectId,
}: ExpenseRowEditorProps) {
  const { t, i18n } = useTranslation();
  const baseField = `expenses.${index}`;
  const data = formStateHandlers.getData();
  const row = data?.expenses?.[index];
  const expenseTypeId = row?.expenseTypeId?.trim() ?? "";
  const selectedExpenseMeta = useMemo(() => expenseTypeOptions.find((e) => e.id === expenseTypeId), [expenseTypeOptions, expenseTypeId]);
  const usesUnitCostPricing = useMemo(() => (selectedExpenseMeta ? u3129ExpenseTypeUnitCost(selectedExpenseMeta) : 0) > 0, [selectedExpenseMeta]);

  /** Aligne `unitCost` / `quantity` sur le type (initLU + chargement GET). */
  useEffect(() => {
    if (readonly) return;
    const currentData = formStateHandlers.getData();
    const list = currentData?.expenses ?? [];
    const currentRow = list[index];
    if (!currentRow) return;

    const et = (currentRow.expenseTypeId ?? "").trim();
    if (!et) {
      const needFix = currentRow.unitCost != null || currentRow.quantity !== undefined;
      if (!needFix) return;
      const updated = list.map((r, i) => (i === index ? { ...r, unitCost: null, quantity: undefined } : r));
      formStateHandlers.setData({ ...currentData, expenses: updated } as U3129Timesheet);
      return;
    }

    const meta = expenseTypeOptions.find((e) => e.id === et);
    const uc = meta ? u3129ExpenseTypeUnitCost(meta) : 0;
    const nextUnitCost = uc > 0 ? uc : null;
    const clearQty = uc === 0;
    const nextQty = clearQty ? undefined : currentRow.quantity;
    if (currentRow.unitCost === nextUnitCost && currentRow.quantity === nextQty) return;

    const updated = list.map((r, i) => (i === index ? { ...r, unitCost: nextUnitCost, quantity: nextQty } : r));
    formStateHandlers.setData({ ...currentData, expenses: updated } as U3129Timesheet);
  }, [readonly, index, formStateHandlers, expenseTypeOptions, expenseTypeId]);

  /** Montant calculé lorsque le coût unitaire > 0. */
  useEffect(() => {
    if (readonly) return;
    const currentData = formStateHandlers.getData();
    const list = currentData?.expenses ?? [];
    const currentRow = list[index];
    if (!currentRow) return;
    const uc = typeof currentRow.unitCost === "number" && Number.isFinite(currentRow.unitCost) ? currentRow.unitCost : 0;
    if (uc <= 0) return;
    const nextAmount = u3129ComputeExpenseLineAmountFromUnit(currentRow);
    const prev = currentRow.amount;
    if (typeof prev === "number" && Math.round(prev * 100) === Math.round(nextAmount * 100)) return;
    const updated = list.map((r, i) => (i === index ? { ...r, amount: nextAmount } : r));
    formStateHandlers.setData({ ...currentData, expenses: updated } as U3129Timesheet);
  }, [readonly, index, formStateHandlers, row?.quantity, row?.unitCost]);

  if (readonly) {
    const rowRead = formStateHandlers.getData()?.expenses?.[index];
    return (
      <tr className="border-b border-gray-100 hover:bg-gray-50/80">
        <td className={U3129_GRID_COMPACT_ROW_CHECKBOX_TD_CLASS}>
          <U2000CheckBox formStateHandlers={formStateHandlers} fieldName={`${baseField}.checked`} label="" readonly={readonly || checkboxDisabled} />
        </td>
        <td className={`px-2 ${U3129_GRID_COMPACT_ROW_CELL_PY} align-top ${U3129_EXPENSE_DATE_COL_CLASS}`}>
          <div className={U3129_READONLY_TABLE_CELL_BOX_COMPACT_CLASS}>{u3129FormatLineDateDisplay(rowRead?.transactionDate, i18n.language)}</div>
        </td>
        <U3129LineProjectSubprojectRowCells
          baseField={baseField}
          arrayField="expenses"
          index={index}
          formStateHandlers={formStateHandlers}
          findProjectHandlers={findProjectHandlers}
          getProjectId={getProjectId}
          readonly={readonly}
          rowIncludeSubProject={rowIncludeSubProject}
          showSubProjectColumn={showSubProjectColumn}
          clearTaskFieldsOnProjectChange={false}
          compactTableControls
          ns={NS}
        />
        <td className={`px-2 ${U3129_GRID_COMPACT_ROW_CELL_PY} align-top ${U3129_EXPENSE_TYPE_COL_CLASS}`}>
          <div className={U3129_READONLY_TABLE_CELL_BOX_COMPACT_CLASS}>
            {u3129ExpenseTypeDisplay(rowRead?.expenseTypeId, expenseTypeOptions)}
          </div>
        </td>
        <td className={`px-1 ${U3129_GRID_COMPACT_ROW_CELL_PY} ${U3129_EXPENSE_UNIT_COL_CLASS} align-top text-right`}>
          {expenseRowShowsUnitCostField(rowRead) ? (
            <div className={U3129_READONLY_NUM_BOX_CLASS}>{u3129FormatExpenseCurrencyCell(rowRead?.unitCost)}</div>
          ) : (
            <div className={U3129_READONLY_TABLE_CELL_BOX_COMPACT_CLASS} />
          )}
        </td>
        <td className={`px-1 ${U3129_GRID_COMPACT_ROW_CELL_PY} ${U3129_EXPENSE_QTY_COL_CLASS} align-top text-right`}>
          <div className={U3129_READONLY_NUM_BOX_CLASS}>
            {rowRead?.quantity != null && typeof rowRead.quantity === "number" && Number.isFinite(rowRead.quantity) && Number.isInteger(rowRead.quantity) ? String(rowRead.quantity) : ""}
          </div>
        </td>
        <td className={`px-1 ${U3129_GRID_COMPACT_ROW_CELL_PY} ${U3129_EXPENSE_AMOUNT_COL_CLASS} align-top text-right`}>
          <div className={U3129_READONLY_NUM_BOX_CLASS}>{u3129FormatExpenseCurrencyCell(rowRead?.amount)}</div>
        </td>
        <td className={`px-2 ${U3129_GRID_COMPACT_ROW_CELL_PY} align-top ${U3129_EXPENSE_TAX_COL_CLASS}`}>
          <div className={U3129_READONLY_TABLE_CELL_BOX_COMPACT_CLASS}>{u3129ExpenseTaxDisplay(rowRead?.taxRateId, taxRateOptions)}</div>
        </td>
        <td className={`px-2 ${U3129_GRID_COMPACT_ROW_CELL_PY} align-top ${U3129_EXPENSE_REMARK_COL_CLASS}`}>
          <div className={U3129_READONLY_TABLE_CELL_BOX_COMPACT_CLASS}>{rowRead?.remark?.trim() || "—"}</div>
        </td>
      </tr>
    );
  }

  return (
    <tr className="border-b border-gray-100 hover:bg-gray-50/80 transition-colors">
      <td className={U3129_GRID_COMPACT_ROW_CHECKBOX_TD_CLASS}>
        <U2000CheckBox formStateHandlers={formStateHandlers} fieldName={`${baseField}.checked`} label="" readonly={readonly || checkboxDisabled} />
      </td>
      <td className={`px-2 ${U3129_GRID_COMPACT_ROW_CELL_PY} align-top ${U3129_EXPENSE_DATE_COL_CLASS}`}>
        <U2000InputCalendarCustom
          formStateHandlers={formStateHandlers}
          fieldName={`${baseField}.transactionDate`}
          label=""
          dateFormat="yyyy-MM-dd"
          compactGrid
        />
      </td>
      <U3129LineProjectSubprojectRowCells
        baseField={baseField}
        arrayField="expenses"
        index={index}
        formStateHandlers={formStateHandlers}
        findProjectHandlers={findProjectHandlers}
        getProjectId={getProjectId}
        readonly={readonly}
        rowIncludeSubProject={rowIncludeSubProject}
        showSubProjectColumn={showSubProjectColumn}
        clearTaskFieldsOnProjectChange={false}
        compactTableControls
        ns={NS}
      />
      <td className={`px-2 ${U3129_GRID_COMPACT_ROW_CELL_PY} align-top ${U3129_EXPENSE_TYPE_COL_CLASS}`}>
        <U2000ComboBox<U3129ExpenseTypeItem>
          formStateHandlers={formStateHandlers}
          fieldName={`${baseField}.expenseTypeId`}
          label=""
          data={expenseTypeOptions}
          keyField="id"
          descField="description"
          formatLabel={(item) => u3129ExpenseTypeLabel(item)}
          findItemByValue={(value, list) => list.find((d) => d.id === value) ?? null}
          getValue={(item) => item.id}
          placeholder={t("placeholderSelectExpenseType", { ns: NS, defaultValue: "Type de dépense" })}
          compactGrid
        />
      </td>
      <td className={`px-1 ${U3129_GRID_COMPACT_ROW_CELL_PY} ${U3129_EXPENSE_UNIT_COL_CLASS} align-top text-right`}>
        {expenseRowShowsUnitCostField(row) ? (
          <div className={U3129_READONLY_NUM_BOX_CLASS}>{u3129FormatExpenseCurrencyCell(row?.unitCost)}</div>
        ) : (
          <div className={U3129_READONLY_TABLE_CELL_BOX_COMPACT_CLASS} />
        )}
      </td>
      <td className={`px-1 ${U3129_GRID_COMPACT_ROW_CELL_PY} ${U3129_EXPENSE_QTY_COL_CLASS} align-top text-right`}>
        {usesUnitCostPricing ? (
          <div className={U3129_GRID_COMPACT_DECIMAL_CONTROL_CLASS}>
            <U2000InputDecimal formStateHandlers={formStateHandlers} fieldName={`${baseField}.quantity`} label="" readonly={false} decimalDigits={0} textAlign="right" />
          </div>
        ) : (
          <div className={`${U3129_READONLY_TABLE_CELL_BOX_COMPACT_CLASS} justify-end tabular-nums`}>
            {row?.quantity != null && typeof row.quantity === "number" && Number.isFinite(row.quantity) && Number.isInteger(row.quantity) ? String(row.quantity) : ""}
          </div>
        )}
      </td>
      <td className={`px-1 ${U3129_GRID_COMPACT_ROW_CELL_PY} ${U3129_EXPENSE_AMOUNT_COL_CLASS} align-top text-right`}>
        {usesUnitCostPricing ? (
          <div className={U3129_READONLY_NUM_BOX_CLASS}>{u3129FormatExpenseCurrencyCell(row?.amount)}</div>
        ) : (
          <div className={U3129_GRID_COMPACT_DECIMAL_CONTROL_CLASS}>
            <U2000InputCurrency formStateHandlers={formStateHandlers} fieldName={`${baseField}.amount`} label="" decimalDigits={2} allowNegative={false} />
          </div>
        )}
      </td>
      <td className={`px-2 ${U3129_GRID_COMPACT_ROW_CELL_PY} align-top ${U3129_EXPENSE_TAX_COL_CLASS}`}>
        <U2000ComboBox<U3129TaxRateItem>
          formStateHandlers={formStateHandlers}
          fieldName={`${baseField}.taxRateId`}
          label=""
          data={taxRateOptions}
          keyField="id"
          descField="description"
          formatLabel={u3129FormatTaxRateComboLabel}
          findItemByValue={(value, list) => list.find((d) => d.id === value) ?? null}
          getValue={(item) => item.id}
          placeholder={t("placeholderSelectTaxRate", { ns: NS, defaultValue: "Taux de taxe" })}
          compactGrid
        />
      </td>
      <td className={`px-2 ${U3129_GRID_COMPACT_ROW_CELL_PY} align-top ${U3129_EXPENSE_REMARK_COL_CLASS}`}>
        <U2000InputText formStateHandlers={formStateHandlers} fieldName={`${baseField}.remark`} label="" />
      </td>
    </tr>
  );
}

const U3129ExpenseTab: React.FC<U3129ExpenseTabProps> = ({ formStateHandlers }) => {
  const { t } = useTranslation();
  const { initLU } = useU3129Context();
  const showSubProjectColumn = useMemo(() => initLU.personalization?.admIncludeSubProject === "true", [initLU.personalization?.admIncludeSubProject]);
  const expenseTableColCount = showSubProjectColumn ? 11 : 10;
  const expenseFooterFirstColSpan = showSubProjectColumn ? 8 : 7;
  const [deletePendingIndices, setDeletePendingIndices] = useState<number[] | null>(null);
  const confirmYesButtonRef = useRef<HTMLButtonElement>(null);
  const findProjectHandlers = useU3129FindProjectHandlers();

  const data = formStateHandlers.getData();
  const readonly = formStateHandlers.getFormState() === U2000FormStateEnumState.READ || (data != null && data.status !== "Open");

  const expenseTypeOptions = useMemo(() => (initLU.expenseTypes ?? []).filter((x) => u3129IsExpenseTypeActive(x)), [initLU.expenseTypes]);

  const taxRateOptions = useMemo(() => {
    const empty: U3129TaxRateItem = {
      id: "",
      code: "",
      rate: 0,
      description: t("lblTaxRateNone", { ns: NS, defaultValue: "—" }),
    };
    const activeRates = (initLU.taxRates ?? []).filter((x) => u3129IsTaxRateActive(x));
    return [empty, ...activeRates];
  }, [initLU.taxRates, t]);

  useEffect(() => {
    const current = formStateHandlers.getData();
    const list = current?.expenses ?? [];
    if (!list.length) return;
    let changed = false;
    const next = list.map((r) => {
      if (r.clientRowId?.trim()) return r;
      changed = true;
      return { ...r, clientRowId: newHonorariumClientRowId() };
    });
    if (changed) {
      formStateHandlers.setData({ ...current, expenses: next } as U3129Timesheet);
    }
  }, [data?.expenses, formStateHandlers]);

  const rows = useMemo(() => data?.expenses ?? [], [data?.expenses]);
  const totalAmount = useMemo(
    () =>
      rows.reduce((sum, row) => {
        const amount = row?.amount;
        return typeof amount === "number" && Number.isFinite(amount) ? sum + amount : sum;
      }, 0),
    [rows],
  );

  const hasCheckedRow = useMemo(() => rows.some((row) => (row as unknown as Record<string, unknown>).checked === true), [rows]);

  useEffect(() => {
    if (deletePendingIndices != null && deletePendingIndices.length > 0) {
      const timer = setTimeout(() => confirmYesButtonRef.current?.focus(), 0);
      return () => clearTimeout(timer);
    }
  }, [deletePendingIndices]);

  const handleAddRow = useCallback(() => {
    const current = formStateHandlers.getData();
    const list = current?.expenses ?? [];
    formStateHandlers.setData({
      ...current,
      expenses: [...list, emptyExpenseRow()],
    } as U3129Timesheet);
  }, [formStateHandlers]);

  const handleDeleteCheckedRow = useCallback(() => {
    const list = formStateHandlers.getData()?.expenses ?? [];
    const indices = list.map((r, i) => ((r as unknown as Record<string, unknown>).checked === true ? i : -1)).filter((i) => i >= 0);
    if (indices.length > 0) setDeletePendingIndices(indices);
  }, [formStateHandlers]);

  const confirmDeleteRow = useCallback(() => {
    if (deletePendingIndices == null || deletePendingIndices.length === 0) return;
    const toRemove = new Set(deletePendingIndices);
    const current = formStateHandlers.getData();
    const list = current?.expenses ?? [];
    const next = list.filter((_, i) => !toRemove.has(i));
    const nextData = {
      ...current,
      expenses: next,
    } as U3129Timesheet;
    formStateHandlers.setData(nextData);
    const validateForm = formStateHandlers.getBusinessHandlers()?.validateForm;
    formStateHandlers.setErrors(validateForm ? validateForm(nextData) : {});
    setDeletePendingIndices(null);
  }, [deletePendingIndices, formStateHandlers]);

  const getProjectIdForRow = useCallback(
    (rowIndex: number) => {
      const r = formStateHandlers.getData()?.expenses?.[rowIndex];
      const proj = r && (r as unknown as Record<string, unknown>).project;
      const pid = proj && typeof proj === "object" && "id" in proj ? String((proj as { id?: string }).id ?? "") : "";
      return r?.projectId?.trim() || pid || undefined;
    },
    [formStateHandlers],
  );

  const getRowIncludeSubProject = useCallback(
    (rowIndex: number): boolean => {
      const r = formStateHandlers.getData()?.expenses?.[rowIndex];
      const project = (r as unknown as Record<string, unknown>)?.project as U3129FindProjectItem | undefined;
      const v = project?.includeSubProjet;
      return v === true || (typeof v === "string" && v === "true");
    },
    [formStateHandlers],
  );

  return (
    <div className="rounded-lg border border-gray-200 bg-muted/10 pt-4 px-4 pb-2">
      <div className="relative flex flex-col gap-4">
        {deletePendingIndices != null && deletePendingIndices.length > 0 && (
          <div
            className="absolute inset-0 z-100 flex items-center justify-center rounded-lg bg-black/30 backdrop-blur-[1px]"
            aria-modal="true"
            role="alertdialog"
            aria-labelledby="confirm-delete-expense-title"
          >
            <div className="relative z-101 mx-4 w-full max-w-md rounded-xl border border-gray-200 bg-white p-5 shadow-lg">
              <h3 id="confirm-delete-expense-title" className="text-lg font-semibold text-gray-900">
                {deletePendingIndices.length === 1
                  ? t("msgConfirmDeleteExpenseRow", { ns: NS, defaultValue: "Supprimer cette ligne ?" })
                  : t("msgConfirmDeleteExpenseRowsBulk", {
                      ns: NS,
                      count: deletePendingIndices.length,
                      defaultValue: "Supprimer {{count}} lignes de dépenses ?",
                    })}
              </h3>
              <p className="mt-2 text-sm text-gray-600">
                {deletePendingIndices.length === 1
                  ? t("msgConfirmDeleteExpenseRowDetail", { ns: NS, defaultValue: "Voulez-vous supprimer cette entrée de la liste des dépenses ?" })
                  : t("msgConfirmDeleteExpenseRowsBulkDetail", {
                      ns: NS,
                      count: deletePendingIndices.length,
                      defaultValue: "Les {{count}} lignes cochées seront retirées de la liste des dépenses.",
                    })}
              </p>
              <div className="mt-4 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setDeletePendingIndices(null)}
                  className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  <FaTimesCircle className="h-4 w-4" />
                  {t("dlgLblNo", { ns: "common", defaultValue: "Non" })}
                </button>
                <button
                  ref={confirmYesButtonRef}
                  type="button"
                  onClick={confirmDeleteRow}
                  className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700"
                >
                  <FaCheck className="h-4 w-4" />
                  {t("dlgLblYes", { ns: "common", defaultValue: "Oui" })}
                </button>
              </div>
            </div>
          </div>
        )}

        {!readonly && (
          <div className="flex justify-start gap-2">
            <U2000ActionBtn label={t("btnAdd", { ns: NS })} icon={FaPlus} handleClick={handleAddRow} />
            <U2000ActionBtn
              label={t("btnDelete", { ns: NS })}
              icon={FaTrash}
              handleClick={handleDeleteCheckedRow}
              disabled={!hasCheckedRow}
              className="bg-red-600! hover:bg-red-700! focus:ring-red-500! active:bg-red-800! disabled:bg-gray-400!"
            />
          </div>
        )}

        <div className={`min-w-0 overflow-x-auto rounded-lg border border-gray-200 ${U3129_GRID_COMPACT_TABLE_CONTROL_CLASS}`}>
          <table className="min-w-full text-sm">
            <thead className="sticky top-0 bg-muted border-b border-gray-200 z-10">
              <tr>
                <th className="w-10 pl-2 pr-1 py-2.5" />
                <th className={`pl-5 pr-2 py-2.5 text-left font-medium text-gray-700 ${U3129_EXPENSE_DATE_COL_CLASS}`}>{t("lblTransactionDate", { ns: NS, defaultValue: "Date" })}</th>
                <th className={`pl-5 pr-3 py-2.5 text-left font-medium text-gray-700 ${U3129_EXPENSE_PROJECT_COL_CLASS}`}>{t("lblProject", { ns: NS })}</th>
                <th className={`pl-5 pr-3 py-2.5 text-left font-medium text-gray-700 ${U3129_EXPENSE_CUSTOMER_COL_CLASS}`}>{t("lblCustomer", { ns: NS })}</th>
                {showSubProjectColumn ? (
                  <th className={`pl-5 pr-3 py-2.5 text-left font-medium text-gray-700 ${U3129_EXPENSE_SUBPROJECT_COL_CLASS}`}>{t("lblSubProject", { ns: NS })}</th>
                ) : null}
                <th className={`pl-5 pr-2 py-2.5 text-left font-medium text-gray-700 ${U3129_EXPENSE_TYPE_COL_CLASS}`}>{t("lblExpenseType", { ns: NS, defaultValue: "Type dépense" })}</th>
                <th className={`px-1 py-2.5 text-right font-medium text-gray-700 pr-3 ${U3129_EXPENSE_UNIT_COL_CLASS}`}>{t("lblExpenseUnitCost", { ns: NS, defaultValue: "Coût unitaire" })}</th>
                <th className={`px-1 py-2.5 text-right font-medium text-gray-700 pr-3 ${U3129_EXPENSE_QTY_COL_CLASS}`}>{t("lblQuantity", { ns: NS, defaultValue: "Qté" })}</th>
                <th className={`px-1 py-2.5 text-right font-medium text-gray-700 pr-3 ${U3129_EXPENSE_AMOUNT_COL_CLASS}`}>{t("lblAmount", { ns: NS, defaultValue: "Montant" })}</th>
                <th className={`pl-5 pr-2 py-2.5 text-left font-medium text-gray-700 ${U3129_EXPENSE_TAX_COL_CLASS}`}>{t("lblTaxRate", { ns: NS, defaultValue: "Taux taxe" })}</th>
                <th className={`pl-5 pr-2 py-2.5 text-left font-medium text-gray-700 ${U3129_EXPENSE_REMARK_COL_CLASS}`}>{t("lblRemark", { ns: NS, defaultValue: "Remarque" })}</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={expenseTableColCount} className="px-4 py-8 text-center text-sm text-gray-400">
                    {t("msgNoExpenseLines", { ns: NS, defaultValue: "Aucune dépense pour cette période." })}
                  </td>
                </tr>
              ) : (
                rows.map((row, idx) => (
                  <ExpenseRowEditor
                    key={row.clientRowId ?? `exp-${idx}`}
                    index={idx}
                    formStateHandlers={formStateHandlers}
                    findProjectHandlers={findProjectHandlers}
                    expenseTypeOptions={expenseTypeOptions}
                    taxRateOptions={taxRateOptions}
                    readonly={readonly}
                    showSubProjectColumn={showSubProjectColumn}
                    rowIncludeSubProject={getRowIncludeSubProject(idx)}
                    getProjectId={() => getProjectIdForRow(idx)}
                  />
                ))
              )}
            </tbody>
            <tfoot className="border-t border-gray-200 bg-gray-50">
              <tr>
                <td colSpan={expenseFooterFirstColSpan} className={`px-2 py-2.5 text-right font-semibold text-gray-700 ${U3129_EXPENSE_TOTAL_LABEL_COL_CLASS}`}>
                  {t("lblTotal", { ns: NS, defaultValue: "Total" })}
                </td>
                <td className="px-1 py-2.5 pr-4 text-right font-semibold text-gray-900 tabular-nums">{formatCurrency(totalAmount, { decimalDigits: 2 })}</td>
                <td colSpan={2} className="px-2 py-2.5" />
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
};

export default U3129ExpenseTab;
